/**
 * @fileoverview Os convites que ainda não viraram conta, em Usuários (etapa 5
 * dos planos): para quem, o perfil, quando vence e quem convidou; reenviar
 * (mais 7 dias, com o link novo por e-mail) e excluir (o link deixa de valer).
 * Reenviar um convite de administrador, ou de licença acima da própria, pede
 * team.manage_access, como convidar; excluir, só team.manage.
 */

import React, { useContext, useState } from "react";
import { toast } from "react-toastify";

import ConfirmationModal from "../ConfirmationModal";
import { errorText } from "../AuthLayout/authHelpers";
import { announceInviteLimit } from "./InviteDialog";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import useInviteStyles from "./styles";

const t = (key, options) => i18n.t(`users.invites.${key}`, options);
const DAY_MS = 24 * 60 * 60 * 1000;

const PendingInvites = ({ invites, onUpdate, onRemove }) => {
  const classes = useInviteStyles();
  const { user } = useContext(AuthContext);
  const { can } = usePermission();
  const canManageAccess = can("team.manage_access", isAdminUser(user));
  const ownLicense = user?.accessScope || "full";
  const [busyId, setBusyId] = useState(null);
  const [revoking, setRevoking] = useState(null);

  if (!invites.length) return null;

  const canResend = (invite) =>
    canManageAccess || (invite.profile !== "admin" && (ownLicense === "full" || invite.accessScope === ownLicense));

  const resend = async (invite) => {
    setBusyId(invite.id);
    try {
      const { data } = await api.post(`/invites/${invite.id}/resend`);
      onUpdate(data);
      toast.success(t("resent", { email: invite.email }));
    } catch (err) {
      // O limite do plano abre o aviso com os planos no lugar do toast.
      if (!announceInviteLimit(err)) toast.error(errorText(err));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async () => {
    const invite = revoking;
    setBusyId(invite.id);
    try {
      await api.post(`/invites/${invite.id}/revoke`);
      onRemove(invite.id);
      toast.success(t("revoked"));
      setRevoking(null);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusyId(null);
    }
  };

  const expiry = (invite) => {
    if (invite.status === "expired") return <span className={classes.expired}>{t("expired")}</span>;
    const count = Math.max(1, Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / DAY_MS));
    return <span>{t("expiresIn", { count })}</span>;
  };

  return (
    <section className={classes.section} aria-labelledby="pending-invites-title">
      <h2 id="pending-invites-title" className={classes.sectionTitle}>{t("pendingTitle", { count: invites.length })}</h2>
      <ul className={classes.list}>
        {invites.map((invite) => (
          <li key={invite.id} className={classes.item}>
            <div className={classes.main}>
              <div className={classes.email} title={invite.email}>{invite.email}</div>
              <div className={classes.meta}>
                {t(`profiles.${invite.profile === "admin" ? "admin" : "user"}`)}
                {", "}
                {expiry(invite)}
                {invite.invitedBy?.name ? `, ${t("invitedBy", { name: invite.invitedBy.name })}` : ""}
              </div>
            </div>
            <div className={classes.actions}>
              <button type="button" className={classes.action} onClick={() => resend(invite)} disabled={busyId === invite.id || !canResend(invite)}>
                {t("resend")}
              </button>
              <button type="button" className={`${classes.action} ${classes.actionDanger}`} onClick={() => setRevoking(invite)} disabled={busyId === invite.id}>
                {t("revoke")}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <ConfirmationModal
        title={t("revokeTitle")}
        open={!!revoking}
        onClose={() => setRevoking(null)}
        onConfirm={revoke}
        confirmLoading={!!revoking && busyId === revoking.id}
        confirmLabel={t("revokeConfirm")}
      >
        {revoking ? t("revokeText", { email: revoking.email }) : ""}
      </ConfirmationModal>
    </section>
  );
};

export default PendingInvites;
